import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Container, TextInput, Select, Text, Button } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAt, IconSend, IconCheck, IconScan, IconChevronRight } from '@tabler/icons-react';
import DateInput from '../components/DateInputES';
import { Row, Col } from 'react-bootstrap';
import PhoneInputGroup from '../components/PhoneInputGroup';
import ModalMailVerification from "../components/ModalMailVerification";
import ModalSMSVerification from "../components/ModalSMSVerification";
import ModalDniScan from "../components/ModalDniScan";
import { SaveResponses } from "../actions/ResponsesActions";
import {
  GetTipoDocumento, GetProfesiones, GetEstadoCivil, GetPaises,
  sendMailValidation, verifyMailValidation, PostZohoLead,
  SendSMS, verifyCodeSMS,
} from "../actions/FormDataActions";
import GenderEnum from '../enums/GenderEnum';
import CellPhoneCountrys from '../enums/CellPhoneCountrys';
import ModalKnownUser from "../components/ModalKnownUser";
import { stepIds } from "../enums/fieldsDataConfig";
import { useStepResponses } from "../hooks/useStepResponses";
import useFormTracking, { identifyUser } from "../hooks/useFormTracking";
import typeConfig from '../config/typeConfig';
import { esEmailValido, esDniValido } from '../utils/validaciones';

// Edad en años cumplidos. Se lee en UTC porque el DatesProvider de DateInputES guarda las fechas como medianoche UTC
const edad = (fechaNacimiento) => {
  const nacimiento = new Date(fechaNacimiento);
  if (Number.isNaN(nacimiento.getTime())) return NaN;
  const hoy = new Date();
  let años = hoy.getUTCFullYear() - nacimiento.getUTCFullYear();
  const meses = hoy.getUTCMonth() - nacimiento.getUTCMonth();
  if (meses < 0 || (meses === 0 && hoy.getUTCDate() < nacimiento.getUTCDate())) años--;
  return años;
};

const isMinor = (fechaNacimiento) => {
  if (!fechaNacimiento) return false;
  const años = edad(fechaNacimiento);
  return Number.isNaN(años) || años < 18;
};

const PersonalDataForm = forwardRef(({ stepNumber,queryParams, id }, ref) => {
  const dispatch = useDispatch();
  
  const response = useSelector((state) => state.respuestas.steps[stepNumber]?.responses || {});
  const summary = useSelector((state) => state.respuestas.checkoutSummary);
  const knownUser = useSelector((state) => state.respuestas.knownUser);
  const {data: dataDocumentos} = useSelector((state) => state.datosFormulario.tiposDoc);
  const {data: dataProfesiones} = useSelector((state) => state.datosFormulario.profesiones);
  const {data: dataEstadosCiviles} = useSelector((state) => state.datosFormulario.estadosCiviles);
  const {data: dataPaises} = useSelector((state) => state.datosFormulario.paises);
  const ProgramResponse = useStepResponses(stepIds.PROGRAMA);
  const { data: dataCarrerasProg } = useSelector((state) => state.carreras.carreras);
  const { data: dataCampusProg } = useSelector((state) => state.carreras.campus);

  const [dniScanOpened, setDniScanOpened] = useState(false);
  const [knownUserOpened, setKnownUserOpened] = useState(false);
  const [knownUserMails, setKnownUserMails] = useState([]);
  const [esReincorporacion, setEsReincorporacion] = useState(false);
  // Reincorporacion en un colegio: no hay formulario en el sitio; el modal dice "contacta a tu colegio".
  const [esReincorporacionColegio, setEsReincorporacionColegio] = useState(false);
  const [knownUserMailSending, setKnownUserMailSending] = useState(false)
  const [knownUserMailSended, setKnownUserMailSended] = useState(false)
  const [selectKey, setSelectKey] = useState(0);
  const { track } = useFormTracking(stepNumber);

  const [mailVerified, setMailVerified] = useState(false);
  const [modalOpenedMail, setModalOpenedMail] = useState(false);
  const [verificationCodeMail, setVerificationCodeMail] = useState('');
  const [sendingVerificationCodeMail, setSendingVerificationCodeMail] = useState(false);
  const [verificationErrorMail, setVerificationErrorMail] = useState(false);
  const [timerMail, setTimerMail] = useState(0);
  const identitySent = useRef(false);
  // Persistido en el response del step (no solo en memoria): este componente se
  // remonta cada vez que se sale y se vuelve a este step (AnimatePresence en
  // CheckoutPage.js), así que un simple useRef(false) se resetea y termina
  // reenviando el lead a Zoho cada vez que el usuario vuelve acá.
  const leadSent = useRef(!!response.zohoLeadSent);

  // SMS OTP del celular: no tiene botón en la UI todavía (ver decisión "solo formato
  // válido" para esta v1), se deja disponible para cuando se necesite activarlo.
  const [smsVerified, setSmsVerified] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState(false);
  const [sendingVerificationCode, setSendingVerificationCode] = useState(false);
  const [timer, setTimer] = useState(0);

  // Helper: setea el campo en el form y emite field_completed (solo Selects no-PII)
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };

  const trackOnBlur = (fieldName) => () => {
    if (form.values[fieldName]) track('field_completed', { fieldName });
  };

  // Tutor obligatorio solo para menores en carreras de grado.
  const tutorRequerido = (values) => isMinor(values.fechaNacimiento) && queryParams.t === 'g';

  const textoTutor = (value, values) =>
    tutorRequerido(values) && !(value?.trim().length > 2) ? "El campo es obligatorio" : null;
  const requeridoTutor = (value, values) =>
    tutorRequerido(values) && !value ? "El campo es obligatorio" : null;

  // Formulario con validación
  const form = useForm({
    initialValues: {
      //persona
      nombres: response.nombres || '',
      apellidos: response.apellidos || '',
      email: response.email || '',
      mailVerified: response.mailVerified || false,
      codAreaCelular: response.codAreaCelular || '+54',
      telefonoCelular: response.telefonoCelular || '',
      documentoNumero: response.documentoNumero || '',
      documentoTipo: response.documentoTipo || 'DNI',
      sexo: response.sexo || null,
      nacionalidad: response.nacionalidad || 'AR',
      fechaNacimiento: response.fechaNacimiento ? new Date(response.fechaNacimiento) : null,
      estadoCivil: response.estadoCivil || null,
      paisNacimiento: response.paisNacimiento || 'AR',
      profesion: response.profesion || null,
      //tutor
      tipoTutor: response.tipoTutor || null,
      paisResidenciaTutor: response.paisResidenciaTutor || 'AR',
      nombresTutor: response.nombresTutor || '',
      apellidosTutor: response.apellidosTutor || '',
      documentoTipoTutor: response.documentoTipoTutor || 'DNI',
      documentoNumeroTutor: response.documentoNumeroTutor || '',
      fechaNacimientoTutor: response.fechaNacimientoTutor ? new Date(response.fechaNacimientoTutor) : null,
    },
    validate: {
      //persona
      nombres: (value) => (value?.length > 2 ? null : "El campo es obligatorio"),
      apellidos: (value) => (value?.length > 2 ? null : "El campo es obligatoria"),
      // Antes el chequeo de formato calculaba el resultado y lo descartaba: un mail mal escrito
      // pasaba sin aviso y el único error posible era "Debe verificar su mail".
      email: (value, values) => {
        if (!esEmailValido(value)) return "El email no es válido";
        if (!values.mailVerified) return "Debe verificar su mail";
        return null;
      },
      telefonoCelular: (value) => {
        const cleaned = value.replace(/\s+/g, "");
        if (!/^\d{10,14}$/.test(cleaned)) return "Debe tener entre 10 y 14 dígitos numéricos";
        if (cleaned.startsWith("0")) return "No debe comenzar con 0";
        return null;
      },
      documentoNumero: (value, values) => {
        if (values.documentoTipo === 'PAS') {
          // Validación para Pasaporte Extranjero: permite letras y números, máximo 12 caracteres
          return /^[A-Za-z0-9]{1,12}$/.test(value) ? null : "El campo debe tener entre 1 y 12 caracteres";
        }
        return esDniValido(value) ? null : "El campo debe tener 7 u 8 dígitos";
      },
      documentoTipo: (value) => (value ? null : "El campo es obligatorio"),
      codAreaCelular: (value) => (value ? null : "El campo es obligatorio"),
      sexo: (value) => (value ? null : "El campo es obligatorio"),
      nacionalidad: (value) => (value ? null : "El campo es obligatorio"),
      fechaNacimiento: (value) => {
        if (!value) return "El campo es obligatorio";
        const años = edad(value);
        
        if (Number.isNaN(años)) return "La fecha no es válida";
        if (años > 100) return "La edad no puede ser mayor a 100 años";
        return null;
      },
      estadoCivil: (value) => (value ? null : "El campo es obligatorio"),
      paisNacimiento: (value) => (value ? null : "El campo es obligatorio"),
      profesion: (value) => (value ? null : "El campo es obligatorio"),
      //tutor
      tipoTutor: requeridoTutor,
      paisResidenciaTutor: requeridoTutor,
      nombresTutor: textoTutor,
      apellidosTutor: textoTutor,
      documentoTipoTutor: requeridoTutor,
      documentoNumeroTutor: (value, values) =>
        tutorRequerido(values) && !esDniValido(value) ? "El campo debe tener 7 u 8 dígitos" : null,
      fechaNacimientoTutor: requeridoTutor,
    },
  });

  const [debouncedValues] = useDebouncedValue(form.values, 300);

  useEffect(() => {
    const currentMailVerified = form.values.mailVerified;
    dispatch(SaveResponses(id, {
      ...debouncedValues,
      mailVerified: currentMailVerified,
    }));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    if (response?.mailVerified) {
      setMailVerified(true);
      form.setFieldValue('mailVerified', true);
    }
  }, [response]);

  useEffect(() => {
    dispatch(GetTipoDocumento());
    dispatch(GetProfesiones());
    dispatch(GetEstadoCivil());
    dispatch(GetPaises());
  }, []);

  useEffect(() => {
    if (timerMail > 0) {
      const interval = setInterval(() => {
        setTimerMail(timerMail - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timerMail]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleSendSMS = () => {
    if (timer > 0) {
      setModalOpened(true);
    } else {
      setSendingVerificationCode(true);
      dispatch(SendSMS(form.values.nombres + form.values.apellidos, form.values.codAreaCelular + form.values.telefonoCelular)).then(() => {
        setSendingVerificationCode(false);
        setTimer(100);
        setModalOpened(true);
        track('field_completed', { fieldName: 'sms_code_sent' });
      });
    }
  };

  const handleVerifyCode = (code) => {
    dispatch(verifyCodeSMS(form.values.nombres + form.values.apellidos, form.values.codAreaCelular + form.values.telefonoCelular, code)).then((isValid) => {
      if (isValid) {
        setSmsVerified(true);
        setModalOpened(false);
        setVerificationError(false);
        track('field_completed', { fieldName: 'sms_verified' });
        dispatch(SaveResponses(id, {
          ...form.values,
          smsVerified: true,
        }));
      } else {
        setVerificationError(true);
        track('field_error', { fieldName: 'sms_verified' });
      }
    });
  };

  useEffect(() => {
    const email = response.email || form.values.email;
    if (mailVerified && ProgramResponse?.carrera && email && !identitySent.current) {
      identitySent.current = true;
      identifyUser(email, {
        periodo:      summary?.fechaIngreso        || null,
        carrera:      ProgramResponse?.carrera      || null,
        localizacion: ProgramResponse?.campus        || null,
        anio:         ProgramResponse?.anioCursada   || null,
        turno:        ProgramResponse?.turno         || null,
        entidad:      queryParams.e,
        tipo:         queryParams.t,
      });
    }
  }, [mailVerified, ProgramResponse, response.email]);

  const buildZohoLeadPayload = (email, phone) => {
    const carreraObj = dataCarrerasProg
      ?.flatMap((f) => f.careers || [])
      ?.find((c) => c.cod === ProgramResponse?.carrera);
    const campusObj = dataCampusProg
      ?.find((c) => c.c_campus_id === ProgramResponse?.campus);

    return {
      First_Name: form.values.nombres,
      Last_Name: form.values.apellidos,
      Email: email,
      Phone: phone || '',
      DNI_Pasaporte: form.values.documentoNumero || '',
      Carrera: carreraObj?.description || ProgramResponse?.carrera || '',
      // Zoho espera SOLO el codigo de carrera; el value del select es "codigo-plan".
      C_digo_de_Carrera: ProgramResponse?.carrera?.split('-')[0] || '',
      Sede: carreraObj?.modalidad || '',
      Localizacion: campusObj?.vc_descripcion || '',
      TipoOferta: typeConfig[queryParams.t]?.shortDescription || '',
      Lead_Source: 'Checkout',
    };
  };

  useEffect(() => {
    const celularLimpio = form.values.telefonoCelular?.replace(/\s+/g, '');
    if (mailVerified && /^\d{10,14}$/.test(celularLimpio || '') && !leadSent.current) {
      leadSent.current = true;
      dispatch(PostZohoLead(buildZohoLeadPayload(form.values.email, form.values.codAreaCelular + form.values.telefonoCelular)));
      dispatch(SaveResponses(id, { zohoLeadSent: true }));
      track('field_completed', { fieldName: 'zoho_lead_sent' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo disparar una vez cuando se cumple la condición
  }, [mailVerified, form.values.telefonoCelular]);

  // Verificar si ya se envió un mail y abrir el modal automáticamente
  useEffect(() => {
    const mailSent = sessionStorage.getItem('mailSentSuccessfully');
    if (mailSent === 'true' && !knownUserOpened) {
      setKnownUserOpened(true);
    }
  }, [knownUserOpened]);

  useEffect(() => {
    if (knownUserOpened) track('field_completed', { fieldName: 'known_user_modal_opened' });
  }, [knownUserOpened]);

  // Limpiar sessionStorage si hay un token válido (usuario ya validado)
  useEffect(() => {
    if (queryParams.token && queryParams.token !== 'undefined') {
      sessionStorage.removeItem('mailSentSuccessfully');
    }
  }, [queryParams.token]);


  const validateUserId = async (tipoDocumento, numeroDocumento) => {

    if (!tipoDocumento || !numeroDocumento || !esDniValido(numeroDocumento))
      return false;
  
    if (queryParams.token && queryParams.token !== 'undefined' && knownUser.comunity)
      return false;
    
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/checkout/user`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
       body: JSON.stringify({ tipoDocumento, numeroDocumento, carrera:ProgramResponse.carrera, tipocarrera:queryParams.t, entidad: queryParams.e }),
      });
  
      if (!response.ok)
        return false;
  
      const data = await response.json();

      const emailsSearchs = data.emails;

      if (!emailsSearchs || !data.emails) {
        return false;
      }

      // Reincorporación: el back avisa si el documento ya tiene legajo en ESTA carrera.
      // El modal cambia a la variante de reincorporación (mail con el link al formulario)
      // y bloquea el checkout para ese DNI. Si el campo no viene (back viejo) queda false.
      setEsReincorporacion(!!data.esReincorporacion);
      setEsReincorporacionColegio(!!data.reincorporacionColegio);
      setKnownUserMails(emailsSearchs);
      return true;

    } catch (error) {
      return false;
    }
  };

  // Vuelta atrás de la variante reincorporación: la única salida del modal es corregir
  // el DNI (por si fue mal tipeado). Se limpia todo el estado de usuario reconocido.
  const handleCorregirDni = () => {
    setKnownUserOpened(false);
    setEsReincorporacion(false);
    setEsReincorporacionColegio(false);
    setKnownUserMails([]);
    form.setFieldValue('documentoNumero', '');
  };

  const handleDniScanned = async (data) => {
    form.setValues({
      documentoTipo: 'DNI',
      documentoNumero: data.documentoNumero,
      nombres: data.nombres,
      apellidos: data.apellidos,
      sexo: data.sexo,
      fechaNacimiento: data.fechaNacimiento,
    });
    setDniScanOpened(false);
    track('field_completed', { fieldName: 'dni_scan' });

    const exists = await validateUserId('DNI', data.documentoNumero);
    if (exists) {
      setSelectKey(k => k + 1);
      setKnownUserOpened(true);
    }
  };


  const sendAutocompleteMail = async (mails) => {
    try {

      const url = `${process.env.REACT_APP_API_BASE_URL}/checkout/user/auto-complete`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documentoTipo: form.values.documentoTipo,
          documentoNumero: form.values.documentoNumero, 
          mails: mails, 
          fecha:ProgramResponse.fechaIngreso,
          carrera:ProgramResponse.carrera,
          campus:ProgramResponse.campus,
          anioCursada:ProgramResponse.anioCursada,
          turno:ProgramResponse.turno,
          t:queryParams.t,
          c:queryParams.c,
          p:queryParams.p,
          fi:queryParams.fi,
          em:queryParams.em,
          entidad: queryParams.e,
        }),
      });
  
      if (!response.ok) {
        if (response.status === 404) {
          console.error("Hubo un error al enviar el mail.");
        }
        return false;
      }
      
      return true;

    } catch (error) {
      return false;
    }
  };

  const handleSaveResponseId = async (idNum) => {
    await form.setFieldValue('documentoNumero', idNum);
  };

  const handleSendMail = async (data) => {
    setKnownUserMailSending(true);
    const result = await sendAutocompleteMail(data);
    setKnownUserMailSending(false);
    if (result) {
      setKnownUserMailSended(true);
      track('field_completed', { fieldName: 'known_user_mail_sent' });

      if (!leadSent.current) {
        leadSent.current = true;
        const phone = form.values.telefonoCelular
          ? form.values.codAreaCelular + form.values.telefonoCelular
          : '';
        dispatch(PostZohoLead(buildZohoLeadPayload(data, phone)));
        dispatch(SaveResponses(id, { zohoLeadSent: true }));
        track('field_completed', { fieldName: 'zoho_lead_sent_known_user' });
      }
    } else {
      track('field_error', { fieldName: 'known_user_mail_sent' });
    }
    return result;
  };

  const handleSendMailCode = () => {
    if (!esEmailValido(form.values.email)) {
      form.setFieldError('email', 'El email no es válido');
      return;
    }
    if (timerMail > 0) {
      setModalOpenedMail(true);
    } else {
      setSendingVerificationCodeMail(true);
      dispatch(sendMailValidation(form.values.email)).then(() => {
        setSendingVerificationCodeMail(false);
        setTimerMail(100);
        setModalOpenedMail(true);
        track('field_completed', { fieldName: 'mail_code_sent' });
      });
    }
  };

  const handleVerifyMailCode = (code) => {
    dispatch(verifyMailValidation(form.values.email, code)).then((isValid) => {
      if (isValid) {
        setMailVerified(true);
        setModalOpenedMail(false);
        setVerificationErrorMail(false);
        track('field_completed', { fieldName: 'mail_verified' });

        dispatch(SaveResponses(id, {
          ...form.values,
          mailVerified: true,
        }));
      } else {
        setMailVerified(false);
        setVerificationErrorMail(true);
        track('field_error', { fieldName: 'mail_verified' });
      }
    });
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

  return (
    <Container size="100%">
      {form.values.documentoTipo === 'DNI' && (
        <Row classlabel="row-inputs">
          <Col md={12}>
            <div
              onClick={() => {
                setDniScanOpened(true);
                track('field_completed', { fieldName: 'dni_scan_button_clicked' });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                background: 'rgba(242, 10, 95, 0.06)',
                border: '0.5px solid rgba(242, 10, 95, 0.25)',
                borderRadius: 8,
                padding: '10px 14px',
              }}
            >
              <IconScan size={20} color="rgb(242, 10, 95)" style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={500} c="rgb(153, 60, 40)">Escanear DNI</Text>
                <Text size="xs" c="dimmed">Completa Nombre, Sexo y Fecha de Nacimiento automáticamente</Text>
              </div>
              <IconChevronRight size={16} color="var(--mantine-color-dimmed)" style={{ marginLeft: 'auto', flexShrink: 0 }} />
            </div>
          </Col>
        </Row>
      )}
      <Row classlabel="row-inputs">
        <Col md={6}>
          <TextInput
            required
            label="Nombres"
            placeholder="Juan"
            {...form.getInputProps("nombres")}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            onBlur={() => { if (form.values.nombres?.length > 2) track('field_completed', { fieldName: 'nombres' }); }}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={6}>
          <TextInput
            required
            label="Apellidos"
            placeholder="Gomez"
            {...form.getInputProps("apellidos")}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            onBlur={() => { if (form.values.apellidos?.length > 2) track('field_completed', { fieldName: 'apellidos' }); }}
            disabled={!!queryParams.token}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs">
        <Col md={3}>
          <Select
            key={selectKey}
            required
            label="Tipo Documento"
            data={dataDocumentos}
            checkIconPosition='right'
            placeholder='Seleccione'
            {...form.getInputProps("documentoTipo")}
            onChange={trackSelect('documentoTipo')}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={3}>
          <TextInput
              id="documentoNumero"
              type={form.values.documentoTipo === 'PAS' ? 'text' : 'number'}
              required
              onKeyDown={(event) => {
                if (form.values.documentoTipo === 'PAS') {
                  // Solo permite letras y números para PAS
                  if (!/^[a-zA-Z0-9]$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                } else {
                  // Si no es PAS, solo permite números
                  if (!/^\d$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }
              }}
              onPaste={e => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('Text').replace(/[^a-zA-Z0-9]/g, '');
                form.setFieldValue('documentoNumero', pasted);
              }}
              onBlur={async () => {
                  const { documentoTipo, documentoNumero } = form.values;
                  const isValid = documentoTipo === 'PAS'
                    ? /^[A-Za-z0-9]{1,12}$/.test(documentoNumero)
                    : esDniValido(documentoNumero);
                  if (isValid) track('field_completed', { fieldName: 'documentoNumero' });

                  if (documentoTipo === 'DNI') {
                    const exists = await validateUserId(documentoTipo, documentoNumero);
                    if (exists) {
                      setSelectKey(k => k + 1);
                      document.body.click();
                      setKnownUserOpened(true);
                    }
                  }
              }}
              placeholder="11 111 111"
              label="Numero Documento"
              value={form.values.documentoNumero}
              onChange={async (event) => {
                const newValue = event.target.value;
                form.setFieldValue("documentoNumero", newValue);
              }}
              error={form.errors.documentoNumero}
              disabled={!!queryParams.token}
            />
        </Col>
        <Col md={6}>
          <fieldset disabled={mailVerified}>
          <TextInput
            required
            label="Email"
            placeholder='usuario@mail.com.ar'
            onPaste={(e) => e.preventDefault()}
            rightSection={mailVerified ? <IconCheck size={20} color="green" /> : <IconAt size={20} />}
            {...form.getInputProps("email")}
            onBlur={() => {
              const email = form.values.email?.trim() || '';
              if (email !== form.values.email) form.setFieldValue('email', email);
              if (!email) return;
              if (esEmailValido(email)) {
                form.clearFieldError('email');
                track('field_completed', { fieldName: 'email' });
              } else {
                form.setFieldError('email', 'El email no es válido');
              }
            }}
            style={mailVerified ? { borderColor: 'green' } : {}}
          />
          </fieldset>
          {mailVerified && (
            <Text size="sm" c="green" mt={4} hiddenFrom="sm">
              <IconCheck size={16} color="green" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
              Mail verificado
            </Text>
          )}
          {!mailVerified && (
            <>
              {timerMail > 0 && (
                <Text size="xs" c="red" mt={4}>
                  Espera {timerMail} segundos para reenviar
                </Text>
              )}
              {/* Solo con un mail bien formado: antes aparecía con cualquier texto de 12 caracteres y el
                  código se intentaba mandar a direcciones inválidas (y no aparecía para mails cortos válidos). */}
              {esEmailValido(form.values.email) && (
                <Button
                  leftSection={<IconSend size={16} />}
                  variant="outline"
                  size="xs"
                  color="rgb(242, 10, 95)"
                  mt={4}
                  onClick={handleSendMailCode}
                  loading={sendingVerificationCodeMail}
                >
                  Enviar mail de confirmacion
                </Button>
              )}
            </>
          )}
        </Col>
      </Row>
      <Row classlabel="row-inputs">
        <Col md={6}>
          <PhoneInputGroup
            label="Teléfono celular"
            placeholder="11 33445566"
            required
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("telefonoCelular")}
            countryCode={form.values.codAreaCelular}
            onPhoneChange={(value) => {
              form.setFieldValue('telefonoCelular', value);
              if (/^\d{10,14}$/.test(value.replace(/\s+/g, ''))) track('field_completed', { fieldName: 'telefonoCelular' });
            }}
            onCountryChange={(value) => form.setFieldValue('codAreaCelular', value)}
            error={form.errors.telefonoCelular}
            countryOptions={CellPhoneCountrys}
          />
        </Col>
        <Col md={6}>
          <Select
            key={selectKey}
            required
            label="Sexo"
            data={GenderEnum}
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("sexo")}
            onChange={trackSelect('sexo')}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs">
        <Col md={4}>
          <Select
            required
            searchable
            label="Nacionalidad"
            data={dataPaises}
            checkIconPosition="right"
            placeholder="Argentina"
            {...form.getInputProps("nacionalidad")}
            onChange={trackSelect('nacionalidad')}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={4}>
          <DateInput
            label="Fecha de Nacimiento"
            required
            maxDate={new Date()}
            placeholder="(dd/mm/aaaa)"
            defaultDate={new Date(2000,0,1)}
            {...form.getInputProps("fechaNacimiento")}
            onChange={(value) => {
              form.setFieldValue('fechaNacimiento', value);
              if (value) track('field_completed', { fieldName: 'fechaNacimiento' });
            }}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label="Situacion Civil"
            searchable
            data={
              dataEstadosCiviles?.map((estado) => {
                const [key, value] = Object.entries(estado)[0];
                return { value, label: estado.vc_descripcion };
              })
            }
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("estadoCivil")}
            onChange={trackSelect('estadoCivil')}
          />
        </Col>
      </Row>
      <Row classlabel="row-inputs" style={{ marginTop: "20px" }}>
        <Col md={6}>
          <Select
            required
            label="País de Nacimiento"
            searchable
            data={dataPaises}
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("paisNacimiento")}
            onChange={trackSelect('paisNacimiento')}
            disabled={!!queryParams.token}
          />
        </Col>
        <Col md={6}>
          <Select
            required
            searchable
            label="Profesión"
            placeholder="Abogado/a"
            flex={1}
            data={
              dataProfesiones?.map((profesion) => {
                const [key, value] = Object.entries(profesion)[0];
                return { value, label: profesion.Descripción };
              })
            }
            checkIconPosition="right"
            {...form.getInputProps("profesion")}
            onChange={trackSelect('profesion')}
          />
        </Col>
      </Row>
      {isMinor(form.values.fechaNacimiento) && queryParams.t === 'g' && (
        <>
          <Text size="xl" fw={500} mb={'xs'} mt={'xl'} c={'rgb(85, 85, 85)'}>Datos del Padre, Madre o Tutor</Text>
          <Row>
            <Col md={6}>
              <Select
                label="Tipo"
                placeholder="Seleccione una opción"
                required
                data={[{ value: 'padre', label: 'Padre' }, { value: 'madre', label: 'Madre' }, { value: 'tutor', label: 'Tutor' }]}
                {...form.getInputProps('tipoTutor')}
                onChange={trackSelect('tipoTutor')}
              />
            </Col>
            <Col md={6}>
              <Select
                required
                label="País de residencia"
                placeholder="Seleccione una opción"
                searchable
                data={dataPaises}
                {...form.getInputProps('paisResidenciaTutor')}
                onChange={trackSelect('paisResidenciaTutor')}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <TextInput
                required
                label="Nombres"
                placeholder="Ingrese los nombres"
                {...form.getInputProps('nombresTutor')}
                onKeyDown={(event) => {
                  if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }}
                onBlur={trackOnBlur('nombresTutor')}
              />
            </Col>
            <Col md={6}>
              <TextInput
                required
                label="Apellidos"
                placeholder="Ingrese los apellidos"
                {...form.getInputProps('apellidosTutor')}
                onKeyDown={(event) => {
                  if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }}
                onBlur={trackOnBlur('apellidosTutor')}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Row>
                <Col md={6} lg={5}>
                  <Select
                    key={selectKey}
                    required
                    label="Tipo de Documento"
                    placeholder="Seleccione una opción"
                    data={dataDocumentos}
                    {...form.getInputProps('documentoTipoTutor')}
                    onChange={trackSelect('documentoTipoTutor')}
                  />
                </Col>
                <Col md={6} lg={7}>
                  <TextInput
                    required
                    type="number"
                    label="Número de Documento"
                    placeholder="Ingrese el número de documento"
                    {...form.getInputProps('documentoNumeroTutor')}
                    onBlur={trackOnBlur('documentoNumeroTutor')}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <DateInput
                label="Fecha de Nacimiento"
                required
                maxDate={new Date()}
                placeholder="(dd/mm/aaaa)"
                {...form.getInputProps('fechaNacimientoTutor')}
                onChange={(value) => {
                  form.setFieldValue('fechaNacimientoTutor', value);
                  if (value) track('field_completed', { fieldName: 'fechaNacimientoTutor' });
                }}
              />
            </Col>
          </Row>
        </>
      )}
      <ModalKnownUser
        opened={knownUserOpened}
        onClose={null}
        knownMails={knownUserMails}
        accept={handleSendMail}
        sendingMail={knownUserMailSending}
        sendedMail={knownUserMailSended}
        esReincorporacion={esReincorporacion}
        esReincorporacionColegio={esReincorporacionColegio}
        onCorregirDni={handleCorregirDni}
      />
      <ModalMailVerification
        isOpen={modalOpenedMail}
        onClose={() => setModalOpenedMail(false)}
        mail={form.values.email}
        timer={timerMail}
        verificationCode={verificationCodeMail}
        setVerificationCode={setVerificationCodeMail}
        onVerify={handleVerifyMailCode}
        onResend={handleSendMailCode}
        verificationError={verificationErrorMail}
      />
      <ModalDniScan
        isOpen={dniScanOpened}
        onClose={() => setDniScanOpened(false)}
        onScanned={handleDniScanned}
      />
      <ModalSMSVerification
        isOpen={modalOpened}
        onClose={() => setModalOpened(false)}
        phoneNumber={form.values.telefonoCelular}
        timer={timer}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        onVerify={handleVerifyCode}
        onResend={handleSendSMS}
        verificationError={verificationError}
      />
    </Container>
  );
});

export default PersonalDataForm;